/**
 * Domain errors come from this registry, never from strings written in place.
 * The codes double as i18n keys, so a message is never hardcoded on the way to a user.
 */
export const ERROR = {
  INVALID_AMOUNT: 'error.invalid_amount',
  INVALID_QUANTITY: 'error.invalid_quantity',
  INVALID_RATE: 'error.invalid_rate',
  INVALID_SCORE: 'error.invalid_score',
  CURRENCY_MISMATCH: 'error.currency_mismatch',
  UNIT_MISMATCH: 'error.unit_mismatch',
  NOT_FOUND: 'error.not_found',
  /**
   * Someone else's row already holds what this one claims. Added in MOL-7: a unique
   * constraint speaks about *other rows* of the table, and the domain — which sees only the
   * input in front of it — has no way to check that. Without this code such a collision came
   * back as a 500, and the path that meets it is an ordinary day rather than a defect:
   * a scanned barcode that already belongs to another item.
   */
  CONFLICT: 'error.conflict',
  /**
   * The request did not name a subject the server could find. Added in MOL-8, and the line
   * between it and CONFLICT is worth keeping: CONFLICT is about *other rows of the table*,
   * which the domain cannot see; NO_ACTOR is about a subject that is absent altogether,
   * which neither the domain nor the database can know — it is a fact of the request.
   *
   * Three cases answer with it and deliberately look identical: no header at all, a header
   * that is not a uuid, and a well-formed uuid with no row behind it. A difference between
   * them is how someone else's identifiers get guessed by comparing replies. It is also what
   * the first visit answers when the invite code is missing or wrong.
   */
  NO_ACTOR: 'error.no_actor',
  /**
   * «Начать поход» while another trip is still open. Added in MOL-21: one open trip per person,
   * and the choice between the two is the person's, not the server's — continue the open one,
   * or finish it and start this. So the server refuses rather than deciding either way, and
   * the screen reads exactly this code to ask.
   */
  TRIP_OPEN: 'error.trip_open',
  TRIP_CONTEXT_REQUIRED: 'error.trip_context_required',
  INTERNAL: 'error.internal',
  LOGIN_UNAVAILABLE: 'error.login_unavailable',
  LOGIN_FORBIDDEN: 'error.login_forbidden',
  LOGIN_RATE_LIMITED: 'error.login_rate_limited',
  LOGIN_DISABLED: 'error.login_disabled',
  BOT_UNAUTHORIZED: 'error.bot_unauthorized',
  /**
   * An exchange dated after today in Yerevan (MOL-40). A rule of the clock, so the use case holds
   * it rather than the schema — the same line `exchangeRateSchema` draws for «not from the future».
   */
  EXCHANGE_IN_FUTURE: 'error.exchange_in_future',
  /** An income dated after today in Yerevan (MOL-66): the same rule of the clock as an exchange. */
  INCOME_IN_FUTURE: 'error.income_in_future',
  /** A spending dated after today in Yerevan (MOL-73): the same rule of the clock again. */
  SPENDING_IN_FUTURE: 'error.spending_in_future',
  /**
   * A spending names a category that is not among the person's own (MOL-73). A removed one is still
   * theirs and still takes spendings — a spending queued offline must not be lost to a category
   * taken out of the choice on another phone in between.
   */
  SPENDING_CATEGORY_UNKNOWN: 'error.spending_category_unknown',
  /** One's own category under a name another live one of theirs already has (MOL-73, В-3). */
  SPENDING_CATEGORY_TAKEN: 'error.spending_category_taken',
  /** An account whose start is dated after today in Yerevan (MOL-115): the rule of the clock again. */
  MONEY_ACCOUNT_IN_FUTURE: 'error.money_account_in_future',
  /** An account under a name another of the owner's — live or removed — already has (MOL-115, Р-21). */
  MONEY_ACCOUNT_TAKEN: 'error.money_account_taken',
  /**
   * A check's fact in another currency than the account's (MOL-115). An operation is never refused
   * over an account: one that does not fit is written «без счёта» (Р-28, Р-31).
   */
  MONEY_ACCOUNT_CURRENCY: 'error.money_account_currency',
  /** The currency of an account that already has operations: they were counted in it (MOL-115). */
  MONEY_ACCOUNT_CURRENCY_LOCKED: 'error.money_account_currency_locked',
  /** A transfer dated after the latest day anywhere on Earth (`latestDay`, MOL-253): the clock again. */
  TRANSFER_IN_FUTURE: 'error.transfer_in_future',
  /**
   * A transfer that names an account it cannot move money on (MOL-253): not the owner's, marked for
   * deletion, or of another currency than the money — given another on a second phone meanwhile. A
   * transfer is written with a connection, never from a queue, so refusing loses nothing; written
   * «без счёта», as an operation from the queue is (Р-28), it would be no transfer at all.
   */
  TRANSFER_ACCOUNT: 'error.transfer_account',
  /**
   * An amendment or a removal of a transfer's fee through «Траты» (MOL-253, Р-5): the fee is written,
   * amended and removed with its transfer, in its sheet, or the two would tell two stories. «Вернуть»
   * of it there finds nothing — 404 — since a fee comes back only with its transfer.
   */
  SPENDING_OF_TRANSFER: 'error.spending_of_transfer',
  /** A code typed by hand not 8, 12 or 13 digits long — the lengths the scanner reads (MOL-98). */
  BARCODE_SHAPE: 'error.barcode_shape',
  /**
   * A code whose last digit does not check: typed by hand, a digit was mistyped (MOL-98); written to
   * the catalogue, it cannot be a package's (MOL-100).
   */
  BARCODE_CHECK_DIGIT: 'error.barcode_check_digit',
  /**
   * A shop's own code — a scale's label, an in-store number (`inStoreBarcode`, MOL-100, В-4): another
   * package is another code, so it is never written to the shared catalogue.
   */
  BARCODE_IN_STORE: 'error.barcode_in_store',
  /** A code added to an item that already holds `ITEM_BARCODES_MAX` of them (MOL-100). */
  BARCODES_FULL: 'error.barcodes_full',
  /**
   * A part of a receipt that is not a photo the reader can take (MOL-125): not a JPEG, or one too
   * small to hold a receipt's print. Answered at once — «не принят» on the phone, never retried.
   */
  RECEIPT_NOT_PHOTO: 'error.receipt_not_photo',
  /** A part of a receipt whose sides pass `RECEIPT_SIDE_MAX`: the phone sends at most 3 200 px. */
  RECEIPT_TOO_LARGE: 'error.receipt_too_large',
  /** «Записать» on a receipt not laid out into lines: still being read, or failed (MOL-126). */
  RECEIPT_NOT_READY: 'error.receipt_not_ready',
  /** The same receipt — its seller's tax number and its number — recorded before, its purchases there (Т-11). */
  RECEIPT_RECORDED_BEFORE: 'error.receipt_recorded_before',
  /**
   * A receipt dated after the latest day anywhere on Earth (`latestDay`, MOL-121 — the phone's today
   * may be a day ahead of Yerevan's) — a day still to come (MOL-126).
   */
  RECEIPT_IN_FUTURE: 'error.receipt_in_future',
  /**
   * «Записать» that would leave a trip with no money and no purchase (MOL-227, Р-4): a receipt with no
   * items whose total was neither read nor typed, or one whose every line is left out with no total —
   * and «Убрать сумму» on a trip with no purchase, which would leave the same (adversarial А4).
   */
  RECEIPT_TOTAL_REQUIRED: 'error.receipt_total_required',
  /**
   * A receipt's link that is no receipt to record (MOL-232): not the tax office's, damaged, or no sale.
   * The phone checks it by the same function before it is queued and says which; the server answers
   * only an old build or a body made by hand.
   */
  RECEIPT_LINK_INVALID: 'error.receipt_link_invalid',
  /**
   * A message to the developer past `FEEDBACK_DAY_LIMIT` in a rolling day (MOL-147). The sheet keeps
   * the text and says to send it tomorrow.
   */
  FEEDBACK_RATE_LIMITED: 'error.feedback_rate_limited',
  /**
   * A picture of a message to the developer that is not one the owner can be sent (MOL-167, Р-2): not
   * a JPEG, or sides outside `FEEDBACK_PICTURE_SIDE_MIN`…`FEEDBACK_PICTURE_SIDE_MAX` or past
   * `FEEDBACK_PICTURE_RATIO_MAX`. The phone draws every picture to fit, so this is another picture to
   * choose — the text and the other pictures stay.
   */
  FEEDBACK_PICTURE_INVALID: 'error.feedback_picture_invalid',
  /** A picture past `FEEDBACK_PICTURE_BYTES_MAX`, or a message's body past `FEEDBACK_BODY_BYTES_MAX`. */
  FEEDBACK_PICTURE_TOO_LARGE: 'error.feedback_picture_too_large',
  /**
   * Failures of the phone past the limit of an address or of everybody's minute (MOL-144, Р-6). The
   * phone shows nothing: it forgets what it kept, since sending it again would change nothing.
   */
  CLIENT_ERRORS_RATE_LIMITED: 'error.client_errors_rate_limited',
} as const

export type ErrorCode = (typeof ERROR)[keyof typeof ERROR]

export class DomainError extends Error {
  readonly code: ErrorCode

  constructor(code: ErrorCode, details?: string) {
    super(details ? `${code}: ${details}` : code)
    this.name = 'DomainError'
    this.code = code
  }
}

/**
 * The same, for zod issues — shape errors, which never become a DomainError. A shape the
 * domain already has a name for reuses that name: a malformed amount is ERROR.INVALID_AMOUNT
 * wherever it is found, and one fact with two keys would be worse than the split is good.
 */
export const ISSUE = {
  BODY_INVALID: 'issue.body_invalid',
  /** A query string that did not parse. Not BODY_INVALID: a `GET` has no body to blame. */
  QUERY_INVALID: 'issue.query_invalid',
  /** An identifier in the path that did not parse — the address is wrong, not the body. */
  PATH_INVALID: 'issue.path_invalid',
  RESPONSE_INVALID: 'issue.response_invalid',
  HEALTH_CONTRADICTS_ITSELF: 'issue.health_contradicts_itself',
  PATCH_EMPTY: 'issue.patch_empty',
  TEXT_NOT_VISIBLE: 'issue.text_not_visible',
  BARCODE_DUPLICATED: 'issue.barcode_duplicated',
  QUANTITY_FRACTIONAL_PIECE: 'issue.quantity_fractional_piece',
  RATE_SAME_CURRENCY: 'issue.rate_same_currency',
  RATE_NOT_OF_TRIP_CURRENCY: 'issue.rate_not_of_trip_currency',
  RATE_IMPLAUSIBLE_DATE: 'issue.rate_implausible_date',
  /**
   * A rate kept beside a snapshot that does not belong there: not the same pair, the wrong
   * source, or beside a snapshot that never jumped (MOL-39).
   */
  SIDE_RATE_UNMATCHED: 'issue.side_rate_unmatched',
  /** A choice of rate on a trip with nothing to choose, or naming a rate the trip does not hold. */
  RATE_CHOICE_NOT_HELD: 'issue.rate_choice_not_held',
  /**
   * A snapshot whose publisher is unknown, or a publisher named for a rate nobody published —
   * none, or the person's own (MOL-22).
   */
  RATE_PROVIDER_UNMATCHED: 'issue.rate_provider_unmatched',
  TRIP_FINISHED_BEFORE_START: 'issue.trip_finished_before_start',
  VERDICT_UPDATED_BEFORE_RATED: 'issue.verdict_updated_before_rated',
  VERDICT_PLACE_NOT_FOR_KIND: 'issue.verdict_place_not_for_kind',
  /** An exchange that gives and receives one currency: nothing was exchanged (MOL-40). */
  EXCHANGE_SAME_CURRENCY: 'issue.exchange_same_currency',
  /** What was held before an exchange, named in a currency other than the one received. */
  EXCHANGE_HELD_NOT_RECEIVED: 'issue.exchange_held_not_received',
  /** What was held before an income, named in a currency other than the one that came in. */
  INCOME_HELD_NOT_RECEIVED: 'issue.income_held_not_received',
  /** A spending's rate snapshot is of another currency than the spending itself (MOL-73). */
  RATE_NOT_OF_SPENDING_CURRENCY: 'issue.rate_not_of_spending_currency',
  /** An account's start in another currency than the account's own (MOL-115). */
  ACCOUNT_START_NOT_OF_CURRENCY: 'issue.account_start_not_of_currency',
  /** «Списано со счёта» with no account to have been taken from (MOL-43 В-3). */
  DEBITED_WITHOUT_ACCOUNT: 'issue.debited_without_account',
  /** A transfer from an account onto itself: nothing moved (MOL-253). */
  TRANSFER_SAME_ACCOUNT: 'issue.transfer_same_account',
  /** A transfer's fee in another currency than the money moved: it is taken from the source (MOL-253). */
  TRANSFER_FEE_NOT_OF_CURRENCY: 'issue.transfer_fee_not_of_currency',
} as const

export type IssueCode = (typeof ISSUE)[keyof typeof ISSUE]
